import axios, { AxiosError, type AxiosInstance, type AxiosResponse } from "axios"
import type { ApiResponse, PaginatedResponse } from "@/types/api"
import type { PortalVenueAddress } from "./training"
import { decodeAttendanceRate, decodePortalAttendanceRecord, type PortalAttendanceRecord } from "@/lib/portal-attendance"
import { fetchAllPages, type AllPages } from "@/lib/paginate"
import { PORTAL_SUPPORTS_PER_PAGE } from "@/lib/portal-documents"
import { PORTAL_EVALUATIONS_MAX_PAGES, PORTAL_EVALUATIONS_PER_PAGE } from "@/lib/portal-evaluations"
import * as O from "fp-ts/Option"
import { extractErrorStatus } from "@/lib/error-messages"
import { isTenantClosedError } from "@/lib/tenant-closed"
import { pipe } from "fp-ts/function"
import { decodeHealthConsentProof, decodeServedConsentText } from "./disability"
import type { HealthConsentProof, HealthConsentText } from "./disability"

// Source unique du type d'adresse de lieu = training.ts (service côté OF).
// Re-exporté ici pour les consommateurs du portail apprenant qui importent
// depuis ce module.
export type { PortalVenueAddress }

const API_BASE_URL = "/api/v1"

/** Connexion du portail qui annonce la fermeture de l'organisme. */
export const PORTAL_CLOSED_LOGIN_PATH = "/portal/login?closed=1"

/**
 * Page où renvoyer l'apprenant après un refus du serveur.
 * Sur 401 : la connexion.
 * Sur 410 TENANT_CLOSED : la connexion annonçant la fermeture.
 *
 * @example portalRedirectFor({ response: { status: 401 } }) // O.some("/portal/login")
 */
export function portalRedirectFor(error: unknown): O.Option<string> {
  if (isTenantClosedError(error)) return O.some(PORTAL_CLOSED_LOGIN_PATH)
  return extractErrorStatus(error) === 401 ? O.some("/portal/login") : O.none
}

/** Profil de l'apprenant, lu et modifié. */
const PORTAL_PROFILE_PATH = "/portal/profile"

// Create a separate axios instance for portal API calls
function createPortalAxiosClient(getToken: () => string): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      // Le middleware CSRF du serveur exige ce header sur toute mutation de
      // `/api/v1/*`, et le portail apprenant n'est pas exempté. Sans lui,
      // signer un émargement ou rendre une évaluation part en 403.
      "X-Requested-With": "XMLHttpRequest",
    },
  })

  // Request interceptor to add portal auth token
  client.interceptors.request.use(
    (config) => {
      const token = getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor for error handling - redirect to portal login, not admin login
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Portal login, NOT admin login. Organisme fermé : la connexion le dit.
      // Already on the closed login page: no reload loop.
      const redirect = portalRedirectFor(error)
      if (O.isSome(redirect)) {
        localStorage.removeItem("portal_token")
        localStorage.removeItem("portal_learner")
        if (`${window.location.pathname}${window.location.search}` !== redirect.value) {
          window.location.href = redirect.value
        }
      }
      return Promise.reject(error)
    }
  )

  return client
}

// Portal Types
export interface PortalSession {
  access_token: string
  token_type: string
  expires_in: number
  learner: PortalLearner
}

/** Partie de `LearnerProfileResponse` lue par le portail. */
export interface PortalProfile {
  first_name: string
  last_name: string
  email: string
}

/** Profil complet de l'apprenant, champs absents décodés en Option. */
export type PortalProfileDetails = {
  firstName: string
  lastName: string
  email: string
  phone: O.Option<string>
  /** Faux : les envois automatiques non obligatoires ne partent pas. */
  emailNotifications: boolean
  hasDisability: boolean
  accommodationsNeeded: O.Option<string>
}

/** Texte non vide d'un champ servi, sinon `O.none`. */
function servedText(value: unknown): O.Option<string> {
  return pipe(
    O.fromNullable(value),
    O.filter((found): found is string => typeof found === "string"),
    O.filter((text) => text.trim() !== ""),
  )
}

/** Décode le profil complet à la frontière. */
function decodeProfileDetails(wire: Record<string, unknown>): PortalProfileDetails {
  return {
    firstName: String(wire.first_name),
    lastName: String(wire.last_name),
    email: String(wire.email),
    phone: servedText(wire.phone),
    emailNotifications: wire.email_notifications_enabled === true,
    hasDisability: wire.has_disability === true,
    accommodationsNeeded: servedText(wire.accommodations_needed),
  }
}

/** Accord de l'apprenant sur ses données de santé. */
export type PortalHealthConsent = {
  /** Absent tant que l'organisme n'a pas complété le texte. */
  text: O.Option<HealthConsentText>
  active: O.Option<HealthConsentProof>
}

/** Préférence modifiable depuis le profil : les e-mails non obligatoires. */
export type PortalPreferencesUpdate = {
  email_notifications_enabled: boolean
}

/** Déclaration de l'apprenant : besoins et, si besoin, son accord. */
export type PortalDisabilityDeclaration = {
  hasDisability: boolean
  accommodationsNeeded: string
  isConsentGiven: boolean
}

export interface PortalLearner {
  id: string
  first_name: string
  last_name: string
  email: string
  registrations: PortalRegistration[]
}

/**
 * Le formateur de la session, tel que l'apprenant a le droit de le connaître.
 *
 * `photo_url` est une route du portail, pas l'URL publique du catalogue : celle-ci
 * reste fermée tant que le formateur n'a pas coché la publication. Absente quand
 * aucune photo n'est chargée — afficher un `<img>` vers un 404 mettrait une image
 * cassée sur la fiche.
 */
export interface PortalTrainer {
  name: string
  bio: string | null
  photo_url: string | null
}

export interface PortalRegistration {
  id: string
  session_id: string
  session_code: string
  program_title: string
  program_description: string
  /** `null` tant que l'organisme n'a pas calé le calendrier (Option<NaiveDate>
   *  côté backend). Ne jamais passer à `new Date` sans garde : `new Date(null)`
   *  vaut le 1er janvier 1970. */
  start_date: string | null
  end_date: string | null
  status: string
  status_label: string
  /** Inscription active, qui émarge (`registration_status_is_active` côté serveur). */
  is_active: boolean
  location: string
  venue: PortalVenueAddress | null
  trainer_name: string
  /** Même relation que `location` ↔ `venue` : le champ à plat reste, la forme
   *  structurée porte bio et photo. `null` si la session n'a pas d'intervenant. */
  trainer: PortalTrainer | null
  /** Minutes certifiées / minutes des feuilles finalisées, en % ; `none` tant
   *  qu'aucune feuille n'est finalisée (jamais 100 % par défaut). */
  attendance_rate: O.Option<number>
  /** Heures certifiées sur les feuilles finalisées ; `none` sans feuille finalisée. */
  certified_hours: O.Option<number>
  finalized_sheet_count: number
  upcoming_slots: PortalSlot[]
  evaluations_pending: number
  evaluations_completed: number
}

export interface PortalSlot {
  id: string
  date: string
  start_time: string
  end_time: string
  title: string
  location: string
  venue: PortalVenueAddress | null
  is_attended: boolean
  attendance_status: string
}

export type { PortalAttendanceRecord }

/** Forme exacte de `PortalEvaluation`. */
export interface PortalEvaluation {
  id: string
  registration_id: string
  title: string
  description: string | null
  /** Code ref_evaluation_type. */
  evaluation_type: string
  /** Libellé ref_evaluation_type, affiché tel quel. */
  evaluation_type_label: string
  status: string
  /** Libellé du statut (ref_evaluation_status), affiché tel quel. */
  status_label: string
  /** Statut non final côté serveur : l'évaluation attend la réponse. */
  awaits_response: boolean
  scheduled_at: string | null
  /** Échéance de réponse. */
  expires_at: string | null
  completed_at: string | null
  /** Note sur 100 : calculée à la soumission d'un modèle noté, ou saisie par l'organisme. */
  overall_score: number | null
  general_comments: string | null
  questions: PortalEvaluationQuestion[]
}

export interface PortalEvaluationQuestion {
  id: string
  question_text: string
  question_type: string
  options: string[]
  required: boolean
  order_index: number
  answer: string | null
}

/** Support de session déposé par l'organisme (`PortalDocument` de learner_portal/documents.rs). */
export interface PortalDocument {
  id: string
  title: string
  /** Catégorie du fichier (`TRAINING_MATERIAL`). */
  document_type: string
  document_type_label: string
  file_name: string
  file_size: number
  mime_type: string
  download_url: string
  created_at: string
  /** Lecture obligatoire, posée par l'organisme. */
  is_required: boolean
  /** Prise de connaissance confirmée pour cette inscription. */
  is_acknowledged: boolean
}

export interface PortalDocumentAcknowledgement {
  document_id: string
  registration_id: string
  acknowledged_at: string
}

/**
 * Document généré visible du stagiaire (certificat de réalisation,
 * attestations, convocation, convention) : la liste blanche est côté serveur
 * (`PORTAL_VISIBLE_DOCUMENTS`). Aucune URL présignée : elle s'obtient au clic
 * par `getGeneratedDocumentDownloadUrl`.
 */
export interface PortalGeneratedDocument {
  id: string
  registration_id: string
  program_title: string
  session_code: string
  template_code: string
  template_label: string
  title: string
  file_name: string
  mime_type: string
  file_size: number | null
  document_number: string | null
  generated_at: string
  /** Exemplaire signé disponible (convention signée). */
  signed: boolean
  download_endpoint: string
}

/** URL présignée émise au clic, de courte durée. */
export interface PortalDocumentDownload {
  url: string
  expires_at: string
}

/** Action attendue du stagiaire (`GET /portal/dashboard`, `requiredActions`). */
export interface PortalRequiredAction {
  id: string
  type: string
  label: string
  /** Chemin du portail où agir (émargement, évaluations). */
  actionUrl: string
  priority: string
}

export interface PortalDashboardStats {
  total_registrations: number
  active_registrations: number
  completed_registrations: number
  pending_evaluations: number
  upcoming_sessions: number
  /** `none` tant qu'aucune feuille d'émargement n'est finalisée (jamais 100 % par défaut). */
  attendance_rate: O.Option<number>
  next_session: PortalSlot | undefined
}

export interface ValidateMagicLinkRequest {
  token: string
}

export interface ValidateMagicLinkResponse {
  valid: boolean
  entity_type: string | null
  entity_id: string | null
  permissions: unknown | null
  context_data: unknown | null
  jwt_token: string | null
}

export interface SignAttendanceRequest {
  signature_data: string
  arrival_time: string
  /** Qualiopi I12: l'apprenant atteste avoir pris connaissance du règlement intérieur. */
  internal_rules_acknowledged?: boolean
}

export interface SubmitEvaluationRequest {
  answers: Record<string, string>
}

/** Inscription décodée au point d'entrée : taux et heures nuls → `none`. */
function decodePortalRegistration(raw: Record<string, unknown>): PortalRegistration {
  return {
    ...(raw as Omit<PortalRegistration, "attendance_rate" | "certified_hours">),
    attendance_rate: decodeAttendanceRate(raw.attendance_rate),
    certified_hours: decodeAttendanceRate(raw.certified_hours),
  }
}

// Portal Service - separate from main API for magic link authentication
class PortalService {
  private portalToken: string = ""
  private client: AxiosInstance

  constructor() {
    const storedToken = localStorage.getItem("portal_token")
    if (storedToken) {
      this.portalToken = storedToken
    }
    // Create axios client that uses portal token
    this.client = createPortalAxiosClient(() => this.portalToken)
  }

  setPortalToken(token: string): void {
    this.portalToken = token
    localStorage.setItem("portal_token", token)
  }

  clearPortalToken(): void {
    this.portalToken = ""
    localStorage.removeItem("portal_token")
    localStorage.removeItem("portal_learner")
  }

  getPortalToken(): string {
    return this.portalToken
  }

  isAuthenticated(): boolean {
    return this.portalToken.length > 0
  }

  // Generic request methods using portal-specific client
  private get<T>(url: string): Promise<T> {
    return this.client.get<T>(url).then((res: AxiosResponse<T>) => res.data)
  }

  private post<T>(url: string, data?: unknown): Promise<T> {
    return this.client.post<T>(url, data).then((res: AxiosResponse<T>) => res.data)
  }

  private patch<T>(url: string, data?: unknown): Promise<T> {
    return this.client.patch<T>(url, data).then((res: AxiosResponse<T>) => res.data)
  }

  // Magic link validation (uses unauthenticated call)
  validateMagicLink(data: ValidateMagicLinkRequest): Promise<ValidateMagicLinkResponse> {
    // Appel non authentifié, mais PAS exempté de CSRF côté serveur : la liste
    // d'exemptions couvre `/api/v1/public/` et `/api/v1/a/`, pas
    // `/api/v1/portal/`. Passer par `axios` nu ici avait fait sauter le header
    // que portent les deux clients configurés, et tout apprenant cliquant son
    // lien magique recevait « CSRF header manquant » au lieu de son espace.
    return this.client
      .post<ValidateMagicLinkResponse>("/portal/validate-magic-link", data)
      .then((res: AxiosResponse<ValidateMagicLinkResponse>) => res.data)
      .then((response) => {
        if (response.valid && response.jwt_token) {
          this.setPortalToken(response.jwt_token)
          // Store entity info for later use
          localStorage.setItem("portal_learner", JSON.stringify({
            id: response.entity_id,
            entity_type: response.entity_type,
          }))
        }
        return response
      })
  }

  logout(): void {
    this.clearPortalToken()
    window.location.href = "/portal/login"
  }

  getCurrentLearner(): PortalLearner | undefined {
    const stored = localStorage.getItem("portal_learner")
    // Guard against invalid JSON (e.g., "undefined" string)
    if (stored && stored !== "undefined") {
      return JSON.parse(stored) as PortalLearner
    }
    return undefined
  }

  // Dashboard/Statistics
  // Taux de présence décodé au point d'entrée : `null` côté API → `none`.
  getDashboard(): Promise<ApiResponse<PortalDashboardStats>> {
    return this.get<ApiResponse<Record<string, unknown>>>("/portal/statistics").then((response) => ({
      data: {
        ...(response.data as Omit<PortalDashboardStats, "attendance_rate">),
        attendance_rate: decodeAttendanceRate(response.data.attendance_rate),
      },
    }))
  }

  // Registrations — toutes les pages (100 par page, plafond de `fetchAllPages`) :
  // une inscription au-delà de la 1re page ne disparaît pas du portail.
  getRegistrations(): Promise<AllPages<PortalRegistration>> {
    return fetchAllPages((page) =>
      this.get<PaginatedResponse<Record<string, unknown>>>(`/portal/registrations?page=${page}&per_page=100`).then(
        (response) => ({ items: response.data.map(decodePortalRegistration), totalPages: response.pagination.total_pages }),
      ),
    )
  }

  getRegistration(id: string): Promise<ApiResponse<PortalRegistration>> {
    return this.get<ApiResponse<Record<string, unknown>>>(`/portal/registrations/${id}`).then((response) => ({
      data: decodePortalRegistration(response.data),
    }))
  }

  // Attendance — séances décodées au point d'entrée (champs absents → Option),
  // toutes les pages (100 par page, plafond de `fetchAllPages`) : la séance du
  // jour reste signable même au-delà de la 50e feuille.
  getAttendance(registrationId: string): Promise<AllPages<PortalAttendanceRecord>> {
    return fetchAllPages((page) =>
      this.get<PaginatedResponse<Record<string, unknown>>>(
        `/portal/registrations/${registrationId}/attendance?page=${page}&per_page=100`
      ).then((response) => ({
        items: response.data.map(decodePortalAttendanceRecord),
        totalPages: response.pagination.total_pages,
      })),
    )
  }

  /** Signe la séance de la feuille `sheetId` (identifiant renvoyé par `getAttendance`). */
  signAttendance(
    registrationId: string,
    sheetId: string,
    data: SignAttendanceRequest
  ): Promise<ApiResponse<PortalAttendanceRecord>> {
    return this.post<ApiResponse<Record<string, unknown>>>(
      `/portal/registrations/${registrationId}/attendance/${sheetId}/sign`,
      data
    ).then((response) => ({ data: decodePortalAttendanceRecord(response.data) }))
  }

  // Evaluations
  /**
   * Toutes les évaluations de l'inscription, lues page par page.
   * Au plus `PORTAL_EVALUATIONS_MAX_PAGES` pages.
   * La pagination rendue est celle du serveur.
   */
  getEvaluations(registrationId: string): Promise<PaginatedResponse<PortalEvaluation>> {
    const readFrom = (
      page: number,
      read: PortalEvaluation[],
    ): Promise<PaginatedResponse<PortalEvaluation>> =>
      this.get<PaginatedResponse<PortalEvaluation>>(
        `/portal/registrations/${registrationId}/evaluations?per_page=${PORTAL_EVALUATIONS_PER_PAGE}&page=${page}`
      ).then((response) => {
        const evaluations = [...read, ...response.data]
        const more = page < response.pagination.total_pages && page < PORTAL_EVALUATIONS_MAX_PAGES
        return more ? readFrom(page + 1, evaluations) : { data: evaluations, pagination: response.pagination }
      })
    return readFrom(1, [])
  }

  getEvaluation(registrationId: string, evaluationId: string): Promise<ApiResponse<PortalEvaluation>> {
    return this.get<ApiResponse<PortalEvaluation>>(
      `/portal/registrations/${registrationId}/evaluations/${evaluationId}`
    )
  }

  submitEvaluation(
    registrationId: string,
    evaluationId: string,
    data: SubmitEvaluationRequest
  ): Promise<ApiResponse<PortalEvaluation>> {
    return this.post<ApiResponse<PortalEvaluation>>(
      `/portal/registrations/${registrationId}/evaluations/${evaluationId}/submit`,
      data
    )
  }

  // Documents
  /** Tous les supports de la session : `per_page` à la borne de dépôt, jamais tronqué. */
  getDocuments(registrationId: string): Promise<PaginatedResponse<PortalDocument>> {
    return this.get<PaginatedResponse<PortalDocument>>(
      `/portal/registrations/${registrationId}/documents?per_page=${PORTAL_SUPPORTS_PER_PAGE}`
    )
  }

  /** Confirme la prise de connaissance d'un support (une fois par inscription). */
  acknowledgeDocument(registrationId: string, documentId: string): Promise<ApiResponse<PortalDocumentAcknowledgement>> {
    return this.post<ApiResponse<PortalDocumentAcknowledgement>>(
      `/portal/registrations/${registrationId}/documents/${documentId}/acknowledge`
    )
  }

  downloadDocument(registrationId: string, documentId: string): Promise<Blob> {
    return this.client.get(`/portal/registrations/${registrationId}/documents/${documentId}/download`, { responseType: "blob" })
      .then((res: AxiosResponse<Blob>) => res.data)
  }

  // Documents générés des inscriptions du stagiaire (filtre facultatif).
  getGeneratedDocuments(registrationId: string): Promise<PaginatedResponse<PortalGeneratedDocument>> {
    const query = new URLSearchParams({ per_page: "100" })
    if (registrationId.length > 0) query.set("registration_id", registrationId)
    return this.get<PaginatedResponse<PortalGeneratedDocument>>(`/portal/documents?${query.toString()}`)
  }

  /** URL présignée d'un document généré, émise au clic (accès tracé côté serveur). */
  getGeneratedDocumentDownloadUrl(documentId: string): Promise<PortalDocumentDownload> {
    return this.get<PortalDocumentDownload>(`/portal/documents/${documentId}/download`)
  }

  /** Actions attendues du stagiaire (émargement du jour, évaluations, supports à lire). */
  getRequiredActions(): Promise<PortalRequiredAction[]> {
    return this.get<{ requiredActions: PortalRequiredAction[] }>("/portal/dashboard").then(
      (response) => response.requiredActions,
    )
  }

  // Profile
  /**
   * Identité de l'apprenant connecté. Le lien magique ne rend que l'identifiant
   * de l'inscription : le nom vient d'ici, jamais du stockage local.
   */
  getProfile(): Promise<PortalProfile> {
    return this.get<ApiResponse<PortalProfile>>(PORTAL_PROFILE_PATH).then((response) => response.data)
  }

  updateProfile(data: unknown): Promise<ApiResponse<unknown>> {
    return this.patch<ApiResponse<unknown>>(PORTAL_PROFILE_PATH, data)
  }

  /** Profil complet : coordonnées, préférences et déclaration d'accessibilité. */
  getProfileDetails(): Promise<PortalProfileDetails> {
    return this.get<ApiResponse<Record<string, unknown>>>(PORTAL_PROFILE_PATH).then((response) =>
      decodeProfileDetails(response.data),
    )
  }

  /** Langue, fuseau horaire et notifications. */
  updatePreferences(preferences: PortalPreferencesUpdate): Promise<void> {
    // comply-ignore: api-route-version-prefix — le client du portail préfixe déjà /api/v1.
    return this.patch<unknown>(`${PORTAL_PROFILE_PATH}/preferences`, preferences).then(() => undefined)
  }

  /** Texte de l'accord sur la santé, et l'accord en cours. */
  getHealthConsent(): Promise<PortalHealthConsent> {
    return this.get<ApiResponse<{ text: unknown; active: unknown }>>("/portal/health-consent").then((response) => ({
      text: decodeServedConsentText(response.data.text),
      active: decodeHealthConsentProof(response.data.active),
    }))
  }

  /** Déclaration d'accessibilité ; l'accord coché l'accompagne. */
  updateDisability(declaration: PortalDisabilityDeclaration): Promise<void> {
    return this.patch<unknown>(`${PORTAL_PROFILE_PATH}/disability`, {
      has_disability: declaration.hasDisability,
      accommodations_needed: declaration.accommodationsNeeded,
      health_consent_given: declaration.isConsentGiven,
    }).then(() => undefined)
  }

  /** Retire l'accord : les données de santé sont effacées aussitôt. */
  withdrawHealthConsent(): Promise<void> {
    return this.post<unknown>("/portal/health-consent/withdraw", {}).then(() => undefined)
  }

  // Direct attendance detail (by attendance ID, not registration-scoped)
  getAttendanceDetail(attendanceId: string): Promise<ApiResponse<PortalAttendanceRecord>> {
    return this.get<ApiResponse<Record<string, unknown>>>(`/portal/attendance/${attendanceId}`)
      .then((response) => ({ data: decodePortalAttendanceRecord(response.data) }))
  }

  signAttendanceDirect(
    attendanceId: string,
    data: SignAttendanceRequest
  ): Promise<ApiResponse<PortalAttendanceRecord>> {
    return this.post<ApiResponse<Record<string, unknown>>>(
      `/portal/attendance/${attendanceId}/sign`,
      data
    ).then((response) => ({ data: decodePortalAttendanceRecord(response.data) }))
  }
}

export const portalService = new PortalService()
